import { supabase } from "./supabase";
import { chaveDoFeito } from "./carteira";
import { slugDeTitulo } from "./slug";
import type {
  Ajuste,
  ExcecaoDeCompetencia,
  Feriado,
  Periodicidade,
  Regra,
  TipoDeDiaUtil,
} from "./calendarioFiscal";

// O IO do Calendário Tributário Federal (000106, 000109).
//
// Este módulo transporta: quem decide se o aluno pode ler é a RLS
// (`conteudo_liberado('calendario-tributario') or eh_admin()` no catálogo, dono
// nos clientes), e quem calcula a DATA é `calendarioFiscal.ts`. Aqui não há
// aritmética de dia útil nem regra de acesso.
//
// ---------------------------------------------------------------------------
// A conversão linha → regra mora AQUI, num lugar só
// ---------------------------------------------------------------------------
// O banco guarda a regra em cinco colunas (`regra_tipo`, `regra_dia`,
// `regra_ajuste`, `regra_n`, `regra_dia_util`) e o módulo puro a consome como
// união discriminada. A tradução entre as duas formas é `regraDe` — e ela é
// única de propósito: espalhar esse `if` pelas telas criaria versões que
// discordam, que é o defeito de "a explicação calculada fora do cálculo" com
// outra roupa.
//
// A constraint `calendario_obrigacoes_regra` garante que toda linha do catálogo
// cai em exatamente uma das três formas — foi para isso que ela existe, depois
// que a 000109 tirou o intérprete de plpgsql. Se `regraDe` receber lixo, é
// porque a constraint foi afrouxada.

export type Setor = "fiscal" | "contabil" | "dp";
export type Regime =
  | "mei"
  | "simples_nacional"
  | "lucro_presumido"
  | "lucro_real"
  | "pessoa_fisica";

export const SETORES: { valor: Setor; rotulo: string }[] = [
  { valor: "fiscal", rotulo: "Fiscal" },
  { valor: "contabil", rotulo: "Contábil" },
  { valor: "dp", rotulo: "Departamento pessoal" },
];

export const REGIMES: { valor: Regime; rotulo: string; curto: string }[] = [
  { valor: "mei", rotulo: "MEI", curto: "MEI" },
  { valor: "simples_nacional", rotulo: "Simples Nacional", curto: "Simples" },
  { valor: "lucro_presumido", rotulo: "Lucro Presumido", curto: "Presumido" },
  { valor: "lucro_real", rotulo: "Lucro Real", curto: "Real" },
  { valor: "pessoa_fisica", rotulo: "Pessoa física", curto: "PF" },
];

/**
 * A descrição desta obrigação repete o que a REGRA já diz?
 *
 * Medido em 25/08 nas 20 linhas carregadas: **dez descrições** repetem o prazo
 * que `explicarRegra` já escreve ao lado, e as quatro maiores do catálogo (392,
 * 358, 319 e 289 caracteres) estão todas nesse grupo. Numa linha do PGDAS-D o
 * contador lê "dia 20, prorrogado para o dia útil seguinte se cair em dia não
 * útil" e depois 392 caracteres que dizem, entre outras coisas, a mesma coisa.
 *
 * Isso é a mesma pergunta da cicatriz do IR Pro — *"conte quantas vezes a mesma
 * informação aparece"* —, e o conserto é de CONTEÚDO: reescrever as descrições é
 * da equipe, não do código, e escondê-las seria pior (a descrição é o que diz o
 * que a obrigação É, e este é um produto de ensino). Então a função não decide
 * nada: ela só ACUSA, na tela de admin, onde a reescrita acontece.
 *
 * Heurística de propósito, e por isso o rótulo na tela diz "parece repetir": o
 * texto do prazo aparece de muitas formas, e um falso positivo aqui custa uma
 * conferência humana de dez segundos — enquanto um falso negativo mantém 392
 * caracteres redundantes na tela de 52 mil contadores.
 */
export function descricaoParecRepetirARegra(descricao: string): boolean {
  if (!descricao) return false;
  return /dia[s]? [úu]til|[úu]ltimo dia|dia 1[0-9]|dia 2[0-9]|dia 3[01]|prazo de entrega|at[ée] o dia|vencimento (é|e) /i.test(
    descricao,
  );
}

export const rotuloDoSetor = (s: string) =>
  SETORES.find((x) => x.valor === s)?.rotulo ?? s;
export const rotuloDoRegime = (r: string) =>
  REGIMES.find((x) => x.valor === r)?.rotulo ?? r;
export const regimeCurto = (r: string) => REGIMES.find((x) => x.valor === r)?.curto ?? r;

export type Obrigacao = {
  id: string;
  slug: string;
  titulo: string;
  descricao: string;
  setor: Setor;
  regra: Regra;
  periodicidade: Periodicidade;
  /** Vale para todo regime — o filtro por regime não a esconde. */
  geral: boolean;
  regimes: Regime[];
  linkAula: string | null;
  ativa: boolean;
  /** Falso enquanto ninguém da equipe conferiu a regra na norma. */
  regraConfirmada: boolean;
  regraOrigem: string | null;
};

export type Cliente = {
  id: string;
  nome: string;
  regime: Regime;
  email: string | null;
  telefone: string | null;
};

type LinhaDeObrigacao = {
  id: string;
  slug: string;
  titulo: string;
  descricao: string | null;
  setor: string;
  regra_tipo: string;
  regra_dia: number | null;
  regra_ajuste: string | null;
  regra_n: number | null;
  regra_dia_util: string | null;
  periodicidade: string;
  periodicidade_mes: number | null;
  geral: boolean;
  regimes: string[] | null;
  link_aula: string | null;
  ativa: boolean;
  regra_confirmada: boolean;
  regra_origem: string | null;
};

/** A regra como o módulo puro a consome. Tradução única, ver o cabeçalho. */
function regraDe(l: LinhaDeObrigacao): Regra | null {
  if (l.regra_tipo === "ultimo-dia-util") return { tipo: "ultimo-dia-util" };
  if (l.regra_tipo === "dia-fixo" && l.regra_dia != null && l.regra_ajuste != null) {
    return { tipo: "dia-fixo", dia: l.regra_dia, ajuste: l.regra_ajuste as Ajuste };
  }
  if (l.regra_tipo === "n-dia-util" && l.regra_n != null && l.regra_dia_util != null) {
    return { tipo: "n-dia-util", n: l.regra_n, diaUtil: l.regra_dia_util as TipoDeDiaUtil };
  }
  return null;
}

function periodicidadeDe(l: LinhaDeObrigacao): Periodicidade | null {
  if (l.periodicidade === "mensal") return { tipo: "mensal" };
  if (l.periodicidade === "anual" && l.periodicidade_mes != null) {
    return { tipo: "anual", mes: l.periodicidade_mes };
  }
  return null;
}

export type Catalogo = {
  obrigacoes: Obrigacao[];
  /** Feriados de exceção, por ano — o nacional é calculado no módulo puro. */
  feriadosExtras: Feriado[];
  feriadosRemovidos: string[];
  /** Decreto por competência, indexado por id de obrigação. */
  excecoes: Map<string, ExcecaoDeCompetencia[]>;
  /**
   * Obrigações que o banco tem e este módulo não soube ler. Só acontece se a
   * constraint da regra for afrouxada; a tela avisa em vez de esconder.
   */
  ilegiveis: number;
};

export async function lerCatalogo(): Promise<Catalogo | null> {
  const [obr, fer, exc] = await Promise.all([
    supabase
      .from("calendario_obrigacoes")
      .select(
        "id,slug,titulo,descricao,setor,regra_tipo,regra_dia,regra_ajuste,regra_n," +
          "regra_dia_util,periodicidade,periodicidade_mes,geral,regimes,link_aula," +
          "ativa,regra_confirmada,regra_origem",
      )
      .order("titulo"),
    supabase.from("calendario_feriados").select("data,nome,acao,abrangencia,uf,municipio"),
    supabase.from("calendario_excecoes").select("obrigacao_id,ano,mes,data,motivo"),
  ]);
  if (obr.error || fer.error || exc.error) return null;

  const obrigacoes: Obrigacao[] = [];
  let ilegiveis = 0;
  for (const l of (obr.data ?? []) as unknown as LinhaDeObrigacao[]) {
    const regra = regraDe(l);
    const periodicidade = periodicidadeDe(l);
    if (!regra || !periodicidade) {
      ilegiveis += 1;
      continue;
    }
    obrigacoes.push({
      id: l.id,
      slug: l.slug,
      titulo: l.titulo,
      descricao: l.descricao ?? "",
      setor: l.setor as Setor,
      regra,
      periodicidade,
      geral: l.geral,
      regimes: (l.regimes ?? []) as Regime[],
      linkAula: l.link_aula,
      ativa: l.ativa,
      regraConfirmada: l.regra_confirmada,
      regraOrigem: l.regra_origem,
    });
  }

  const feriadosExtras: Feriado[] = [];
  const feriadosRemovidos: string[] = [];
  for (const f of fer.data ?? []) {
    if (f.acao === "remover") feriadosRemovidos.push(f.data as string);
    else {
      feriadosExtras.push({
        data: f.data as string,
        nome: f.nome as string,
        nacional: f.abrangencia === "nacional",
      });
    }
  }

  const excecoes = new Map<string, ExcecaoDeCompetencia[]>();
  for (const e of exc.data ?? []) {
    const lista = excecoes.get(e.obrigacao_id as string) ?? [];
    lista.push({
      ano: e.ano as number,
      mes: e.mes as number,
      data: (e.data as string | null) ?? null,
      motivo: e.motivo as string,
    });
    excecoes.set(e.obrigacao_id as string, lista);
  }

  return { obrigacoes, feriadosExtras, feriadosRemovidos, excecoes, ilegiveis };
}

export async function lerClientes(): Promise<Cliente[] | null> {
  const { data, error } = await supabase
    .from("calendario_clientes")
    .select("id,nome,regime,email,telefone")
    .order("nome");
  if (error) return null;
  return (data ?? []) as Cliente[];
}

// ---------------------------------------------------------------------------
// As marcas de FEITO
// ---------------------------------------------------------------------------
// Cuidado com o vocabulário: a coluna se chama `competencia`, mas o que ela
// guarda é o **mês em que a obrigação VENCE** — o mês que a tela mostra e em que
// o contador clica. Para um contador, "competência" é o período a que o tributo
// se refere, que é outra coisa: o DAS que vence em 22/06 é da competência de
// MAIO. Os dois sentidos não podem se misturar, então aqui o nome é
// `mesDoVencimento` e a conversão para a coluna acontece na fronteira. A
// interface nunca escreve a palavra "competência" para o aluno.
export type MesDoVencimento = { ano: number; mes: number };

const paraColuna = (m: MesDoVencimento) =>
  `${m.ano}-${String(m.mes).padStart(2, "0")}-01`;

/**
 * As marcas de feito do MÊS INTEIRO, de todos os clientes do aluno, numa
 * consulta só — com a chave composta `cliente|obrigacao`.
 *
 * A primeira versão era `lerFeitos(clienteId, mes)`, uma leitura por cliente.
 * Media-se assim: um contador com 40 clientes fechando o mês fazia 40 idas à
 * rede só para SABER o que já estava feito, e a tela só sabia responder sobre um
 * cliente por vez — a pergunta que ele faz todo dia 18 ("quais dos meus 40 ainda
 * não entregaram a DCTFWeb?") não tinha resposta, embora o dado sempre a
 * sustentasse.
 *
 * Não precisa de filtro por aluno: a policy `calendario_feito_dono` já resolve a
 * posse por `EXISTS` em `calendario_clientes` com `meu_profile_id()`. Filtrar
 * aqui de novo seria escrever a regra de acesso no front, que é defeito — mas
 * note a direção: o front não pode AFROUXAR, e também não deve REPETIR.
 */
export async function lerFeitosDoMes(mes: MesDoVencimento): Promise<Set<string> | null> {
  const { data, error } = await supabase
    .from("calendario_feito")
    .select("cliente_id,obrigacao_id")
    .eq("competencia", paraColuna(mes));
  if (error) return null;
  return new Set(
    (data ?? []).map((l) => chaveDoFeito(l.cliente_id as string, l.obrigacao_id as string)),
  );
}

export type Escrita = { ok: true } | { ok: false; mensagem: string };

function falha(mensagem: string | undefined): Escrita {
  return {
    ok: false,
    mensagem: mensagem?.trim()
      ? mensagem
      : "Não foi possível concluir agora. Tente de novo em instantes.",
  };
}

export async function marcar(
  clienteId: string,
  obrigacaoId: string,
  mes: MesDoVencimento,
  feito: boolean,
): Promise<Escrita> {
  const { error } = await supabase.rpc("calendario_marcar", {
    p_cliente: clienteId,
    p_obrigacao: obrigacaoId,
    p_competencia: paraColuna(mes),
    p_feito: feito,
  });
  return error ? falha(error.message) : { ok: true };
}

/**
 * Marca (ou desmarca) uma obrigação para VÁRIOS clientes — numa transação só
 * (`calendario_marcar_lote`, 000112).
 *
 * Não é um laço de `marcar` no navegador, e a diferença é de confiança, não de
 * conforto: doze chamadas com a sétima caindo deixam seis marcas gravadas, a
 * tela tendo de escriturar "6 de 12" e o contador precisando de doze cliques
 * para desfazer. Num checklist em que ele aposta multa, lote sem volta atrás é
 * motivo para não confiar na marca — e marca em que não se confia é pior que
 * marca nenhuma, porque ele para de conferir na fonte.
 *
 * Devolve quantas linhas MUDARAM de verdade, para a tela não afirmar trabalho
 * que não houve: reaplicar o mesmo lote devolve zero.
 */
export async function marcarLote(
  obrigacaoId: string,
  mes: MesDoVencimento,
  clienteIds: string[],
  feito: boolean,
): Promise<(Escrita & { mudadas?: number }) | Escrita> {
  const { data, error } = await supabase.rpc("calendario_marcar_lote", {
    p_obrigacao: obrigacaoId,
    p_competencia: paraColuna(mes),
    p_clientes: clienteIds,
    p_feito: feito,
  });
  if (error) return falha(error.message);
  return { ok: true, mudadas: typeof data === "number" ? data : clienteIds.length };
}

export async function salvarCliente(c: {
  id?: string | null;
  nome: string;
  regime: Regime;
  email?: string | null;
  telefone?: string | null;
}): Promise<Escrita> {
  const { error } = await supabase.rpc("calendario_cliente_salvar", {
    p_id: c.id ?? null,
    p_nome: c.nome,
    p_regime: c.regime,
    p_email: c.email?.trim() ? c.email.trim() : null,
    p_telefone: c.telefone?.trim() ? c.telefone.trim() : null,
  });
  return error ? falha(error.message) : { ok: true };
}

export async function excluirCliente(id: string): Promise<Escrita> {
  const { error } = await supabase.rpc("calendario_cliente_excluir", { p_id: id });
  return error ? falha(error.message) : { ok: true };
}

// ---------------------------------------------------------------------------
// O lado do ADMIN — escrita direta na tabela, com a RLS de `pode_editar('calendario')` (000297)
// ---------------------------------------------------------------------------
// Aqui não há RPC porque não há regra a aplicar: o admin edita o catálogo, e o
// que ele pode gravar é decidido pelas constraints (a forma da regra) e pela
// policy (quem é). RPC serviria para esconder decisão, e não há decisão a
// esconder — diferente das escritas do aluno, onde a posse tem de vir de
// `meu_profile_id()` e nunca de um parâmetro.

export type RegraParaGravar = {
  regra_tipo: string;
  regra_dia: number | null;
  regra_ajuste: string | null;
  regra_n: number | null;
  regra_dia_util: string | null;
  periodicidade: string;
  periodicidade_mes: number | null;
};

/** A regra tipada virando as cinco colunas — o caminho inverso de `regraDe`. */
export function colunasDaRegra(regra: Regra, periodicidade: Periodicidade): RegraParaGravar {
  return {
    regra_tipo: regra.tipo,
    regra_dia: regra.tipo === "dia-fixo" ? regra.dia : null,
    regra_ajuste: regra.tipo === "dia-fixo" ? regra.ajuste : null,
    regra_n: regra.tipo === "n-dia-util" ? regra.n : null,
    regra_dia_util: regra.tipo === "n-dia-util" ? regra.diaUtil : null,
    periodicidade: periodicidade.tipo,
    periodicidade_mes: periodicidade.tipo === "anual" ? periodicidade.mes : null,
  };
}

export async function salvarObrigacao(
  id: string,
  campos: Partial<RegraParaGravar> & {
    titulo?: string;
    descricao?: string;
    setor?: Setor;
    geral?: boolean;
    regimes?: Regime[];
    ativa?: boolean;
    regra_confirmada?: boolean;
    regra_origem?: string | null;
  },
): Promise<Escrita> {
  // O `slug` NÃO está entre os campos aceitos, e isso é decisão, não esquecimento:
  // ele é a identidade que `etl/carregar-calendario.mjs` usa no `on conflict`.
  // Renomear o título e regerar o slug faria a próxima carga não achar a linha e
  // CRIAR outra — a obrigação apareceria duas vezes na tela do aluno, com regras
  // diferentes. Título é rótulo; slug é identidade, e identidade não se edita.
  const { error } = await supabase
    .from("calendario_obrigacoes")
    .update(campos)
    .eq("id", id);
  return error ? falha(error.message) : { ok: true };
}

/**
 * Cadastra uma obrigação NOVA — a que não veio da carga.
 *
 * Existiu uma lacuna aqui até 25/08: a tela de admin sabia editar as 20 que o ETL
 * trouxe e não sabia criar a 21ª. Quem descobriu foi Victor, perguntando "como os
 * admins cadastram novas obrigações?" — e a resposta era "não cadastram", embora a
 * policy `calendario_obrigacoes_admin_insert` já existisse no banco desde a
 * 000106. Tela que não faz o que o banco permite é lacuna, não desenho.
 *
 * O `slug` é DERIVADO do título por `slugDeTitulo`, o mesmo módulo que o ETL usa —
 * se as duas pontas derivassem de formas diferentes, a próxima carga deixaria de
 * casar por `on conflict (slug)` e duplicaria o catálogo.
 *
 * Duas recusas antes de tocar no banco, porque a mensagem do Postgres não serve
 * para humano:
 *
 *   · título que não produz slug (só pontuação, só símbolo) — dois títulos assim
 *     colidiriam entre si num `slug` que é NOT NULL UNIQUE;
 *   · slug que já existe — dizer "já existe uma obrigação com este título" é
 *     acionável; `duplicate key value violates unique constraint` não é.
 */
export async function criarObrigacao(dados: {
  titulo: string;
  descricao: string;
  setor: Setor;
  regra: Regra;
  periodicidade: Periodicidade;
  geral: boolean;
  regimes: Regime[];
  confirmada: boolean;
}): Promise<(Escrita & { slug?: string }) | Escrita> {
  const titulo = dados.titulo.trim();
  const slug = slugDeTitulo(titulo);
  if (!slug) {
    return falha(
      "O título precisa ter ao menos uma letra ou número — é dele que sai o identificador da obrigação.",
    );
  }

  const { data: jaTem, error: erroDeLeitura } = await supabase
    .from("calendario_obrigacoes")
    .select("titulo")
    .eq("slug", slug)
    .maybeSingle();
  if (erroDeLeitura) return falha(erroDeLeitura.message);
  if (jaTem) {
    return falha(
      `Já existe uma obrigação com este título ("${(jaTem as { titulo: string }).titulo}"). ` +
        "Se for outra obrigação, diferencie o título; se for a mesma, edite a que existe.",
    );
  }

  const { error } = await supabase.from("calendario_obrigacoes").insert({
    slug,
    titulo,
    descricao: dados.descricao.trim(),
    setor: dados.setor,
    ...colunasDaRegra(dados.regra, dados.periodicidade),
    geral: dados.geral,
    regimes: dados.regimes,
    ativa: true,
    regra_confirmada: dados.confirmada,
    // A procedência entra escrita, como nas 20 da carga: obrigação sem
    // `regra_origem` é a resposta sem a razão, que é o defeito que o porte
    // inteiro existe para consertar.
    regra_origem: dados.confirmada
      ? "cadastrada à mão pela equipe de conteúdo, com a norma conferida"
      : "cadastrada à mão pela equipe de conteúdo, ainda sem conferência na norma",
  });
  return error ? falha(error.message) : { ok: true, slug };
}

/** Quantas marcas de feito existem nesta obrigação, em toda a base. */
export async function contarFeitosDaObrigacao(id: string): Promise<number | null> {
  const { count, error } = await supabase
    .from("calendario_feito")
    .select("obrigacao_id", { count: "exact", head: true })
    .eq("obrigacao_id", id);
  if (error) return null;
  return count ?? 0;
}

/**
 * Exclui uma obrigação — e a tela só oferece isto quando ela NÃO tem marca de
 * feito nenhuma.
 *
 * A FK de `calendario_feito` é `on delete cascade`, então excluir uma obrigação
 * usada apagaria o trabalho registrado de contadores que nunca vão saber por quê.
 * É o princípio da assimetria: errar para mais (deixar uma obrigação inativa na
 * lista do admin) é visível e reversível; errar para menos (apagar marca de
 * entrega) não tem volta e não deixa rastro.
 *
 * Por isso o caminho normal é **desativar** (`ativa = false`), que já existe e
 * tira a obrigação da tela do aluno sem destruir nada. Excluir serve para o
 * cadastro feito por engano, minutos antes.
 *
 * A contagem é conferida na TELA, e a policy `calendario_obrigacoes_admin_delete`
 * é quem realmente decide quem pode — a conferência aqui é sobre o que se perde,
 * não sobre quem manda.
 */
export async function excluirObrigacao(id: string): Promise<Escrita> {
  const feitos = await contarFeitosDaObrigacao(id);
  if (feitos === null) return falha("Não conseguimos conferir se há entregas marcadas.");
  if (feitos > 0) {
    return falha(
      `Esta obrigação já tem ${feitos} ${feitos === 1 ? "entrega marcada" : "entregas marcadas"} ` +
        "por contadores. Desative-a em vez de excluir — excluir apagaria esse registro.",
    );
  }
  const { error } = await supabase.from("calendario_obrigacoes").delete().eq("id", id);
  return error ? falha(error.message) : { ok: true };
}

export async function salvarFeriado(f: {
  data: string;
  nome: string;
  acao: "acrescentar" | "remover";
  abrangencia: "nacional" | "estadual" | "municipal" | "facultativo";
  uf?: string | null;
  municipio?: string | null;
}): Promise<Escrita> {
  const { error } = await supabase.from("calendario_feriados").insert({
    data: f.data,
    nome: f.nome,
    acao: f.acao,
    abrangencia: f.abrangencia,
    uf: f.uf?.trim() ? f.uf.trim().toUpperCase() : null,
    municipio: f.municipio?.trim() ? f.municipio.trim() : null,
  });
  return error ? falha(error.message) : { ok: true };
}

export async function excluirFeriado(data: string, nome: string): Promise<Escrita> {
  const { error } = await supabase
    .from("calendario_feriados")
    .delete()
    .eq("data", data)
    .eq("nome", nome);
  return error ? falha(error.message) : { ok: true };
}

export async function salvarExcecao(e: {
  obrigacaoId: string;
  ano: number;
  mes: number;
  data: string | null;
  motivo: string;
}): Promise<Escrita> {
  const { error } = await supabase
    .from("calendario_excecoes")
    .upsert(
      {
        obrigacao_id: e.obrigacaoId,
        ano: e.ano,
        mes: e.mes,
        data: e.data,
        motivo: e.motivo,
      },
      { onConflict: "obrigacao_id,ano,mes" },
    );
  return error ? falha(error.message) : { ok: true };
}

export async function excluirExcecao(
  obrigacaoId: string,
  ano: number,
  mes: number,
): Promise<Escrita> {
  const { error } = await supabase
    .from("calendario_excecoes")
    .delete()
    .eq("obrigacao_id", obrigacaoId)
    .eq("ano", ano)
    .eq("mes", mes);
  return error ? falha(error.message) : { ok: true };
}
